import React, { useState } from 'react';
import dayjs from 'dayjs';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import { useFormik } from 'formik';
import { visaStatusOptions } from './option';
import { type ApplicationVisaDetail } from '../../graphql';
import { AR_LIST_TEXT, QUALIFICATION_PAGE_TEXT } from '../../lang';
import { checkInvalidDate } from '../../../Application/graphql';
import {
  Button,
  DatePicker,
  type FileData,
  TextField,
  Select,
  type AlertDetails,
  Alert,
} from '../../../../components';
import { downloadFile } from '../../../../utils';
import { useAuth } from '../../../../contexts';

type VisaApplicationFormProps = {
  viewMode: boolean;
  applicationVisaDetail: ApplicationVisaDetail;
  handleOverWrite?: (value: ApplicationVisaDetail) => Promise<void>;
};

export const VisaApplicationForm: React.FunctionComponent<
  VisaApplicationFormProps
> = (props: VisaApplicationFormProps) => {
  const {
    viewMode = false,
    applicationVisaDetail,
    handleOverWrite = () => {},
  } = props;
  const { handleLoading } = useAuth();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const initialDoc: FileData = { link: '', name: 'N/A', id: '' };
  const { entrySlipDocument, visaDocument } = applicationVisaDetail;

  const formik = useFormik<ApplicationVisaDetail>({
    initialValues: applicationVisaDetail,
    enableReinitialize: true,
    onSubmit: handleOverWrite,
  });

  const info = [
    {
      title: AR_LIST_TEXT.APPLICATION_NO,
      value: (
        <TextField
          id="applicationNo"
          name="applicationNo"
          fullWidth
          value={formik.values.applicationNo}
          onBlur={formik.handleBlur}
          InputLabelProps={{ shrink: true }}
          disabled
          sx={{ backgroundColor: '#f5f5f5' }}
        />
      ),
    },
    {
      title: AR_LIST_TEXT.VISA_REF,
      value: (
        <TextField
          id="referenceNo"
          name="referenceNo"
          value={formik.values.referenceNo}
          fullWidth
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={viewMode}
          InputLabelProps={{ shrink: true }}
        />
      ),
    },
    {
      // application receive date
      title: AR_LIST_TEXT.APPLICATION_RECEIVED_DATE,
      value: (
        <DatePicker
          id="applicationReceivedDate"
          name="applicationReceivedDate"
          sx={{
            width: '100%',
          }}
          value={
            checkInvalidDate(formik.values.applicationReceivedDate)
              ? null
              : dayjs(formik.values.applicationReceivedDate)
          }
          disabled={viewMode}
          onChange={date => {
            void formik.setFieldValue('applicationReceivedDate', date);
          }}
          onBlur={formik.handleBlur}
          InputLabelProps={{ shrink: true }}
        />
      ),
    },
    {
      // submit to immd date
      title: AR_LIST_TEXT.SUBMISSION_DATE_TO_IMMD,
      value: (
        <DatePicker
          id="submissionDateToImmd"
          name="submissionDateToImmd"
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{
            width: '100%',
          }}
          value={
            checkInvalidDate(formik.values.submissionDateToImmd)
              ? null
              : dayjs(formik.values.submissionDateToImmd)
          }
          disabled={viewMode}
          onChange={date => {
            void formik.setFieldValue('submissionDateToImmd', date);
          }}
          onBlur={formik.handleBlur}
        />
      ),
    },
    {
      // visa label approved date
      title: AR_LIST_TEXT.VISA_LABEL_APPROVAL_DATE,
      value: (
        <DatePicker
          id="visaLabelApprovalDate"
          name="visaLabelApprovalDate"
          value={
            checkInvalidDate(formik.values.visaLabelApprovalDate)
              ? null
              : dayjs(formik.values.visaLabelApprovalDate)
          }
          sx={{
            width: '100%',
          }}
          disabled={viewMode}
          onChange={date => {
            void formik.setFieldValue('visaLabelApprovalDate', date);
          }}
          onBlur={formik.handleBlur}
        />
      ),
    },
    {
      title: AR_LIST_TEXT.VISA_STATUS,
      value: (
        <Select
          id="status"
          name="status"
          fullWidth
          InputLabelProps={{ shrink: true }}
          options={visaStatusOptions}
          disabled={viewMode}
          onChange={formik.handleChange}
          value={formik.values.status}
          onBlur={formik.handleBlur}
        />
      ),
    },
  ];
  const docs = [
    {
      file: QUALIFICATION_PAGE_TEXT.VISA_DETAIL_TEXT.ENTRY_SLIP,
      value: entrySlipDocument ?? initialDoc,
    },
    {
      file: QUALIFICATION_PAGE_TEXT.VISA_DETAIL_TEXT.VISA_DOC,
      value: visaDocument ?? initialDoc,
    },
  ];

  const handleDownLoadFile = async (
    fileId: string,
    fileName: string,
  ): Promise<void> => {
    await handleLoading(async () => {
      try {
        await downloadFile(fileId, 'pdf', fileName);
      } catch (error) {
        setOpenAlert(true);
        setAlertDetails({
          alertType: 'warning',
          alertContent: 'Error Occurs.' + (error as Error)?.message,
        });
      }
    });
  };

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />

      <div className="mt-8">
        {info.map((row, index) => {
          const { title, value } = row;
          return (
            <div
              key={index}
              className="grid grid-cols-2 px-12 mt-2 justify-items-start h-16 items-start"
            >
              <div className="font-semibold mt-1">{title}</div>
              <div className="w-4/5">{value}</div>
            </div>
          );
        })}
      </div>

      <div className="my-4">
        {docs.map((doc, index) => {
          const { file, value } = doc;
          const { link, name, id } = value;
          const emptyFile = link === '';
          return (
            <div className="flex items-start mt-5 px-12" key={index}>
              <FileOpenIcon className="text-[#951d1d]" />
              <span className="ml-2  text-[#951d1d]">{file}</span>
              {emptyFile ? (
                <i>&nbsp;{QUALIFICATION_PAGE_TEXT.VISA_DETAIL_TEXT.NA}</i>
              ) : (
                <a
                  className="underline italic"
                  onClick={async () => {
                    await handleDownLoadFile(id, name);
                  }}
                >
                  &nbsp;{name}
                </a>
              )}
            </div>
          );
        })}
      </div>
      {!viewMode && (
        <div className="submit-area">
          <Button variant="contained" type="submit">
            {QUALIFICATION_PAGE_TEXT.SAVE}
          </Button>
        </div>
      )}
    </form>
  );
};
